import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import PatientStyle from './problem.module.scss';
import CustomTextField from '../components/form/CustomTextField';
import { toast } from 'react-toastify';
import { addPatient, postalApi, updatePatient } from '../../apis/patientSlice';
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import SearchClinic from '../../components/Autocomplete/SearchClinic';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export default function PatientDialog(props) {
  const { open, setOpen, editData, operationMode, setOperationMode, callApi } = props;
  const dispatch = useDispatch();

  const { loading } = useSelector((state) => state.problemData);

  const [patientData, setPatientData] = useState({
    name: '',
    phone: '',
    gender: 'Male',
    age: '',
    occupation: '',
    address: '',
    pincode: '',
    city: '',
    state: '',
    area: null,
    clinicId: null,
  });
  const [areaOptions, setAreaOptions] = useState([]);
  const [errors, setErrors] = useState({
    phone: '',
  });

  const phoneRegex = /^[6-9]\d{9}$/;

  const normalizePatient = (data = {}) => ({
    name: data.name ?? '',
    phone: data.phone ?? '',
    gender: data.gender || 'Male',
    age: data.age ?? '',
    occupation: data.occupation ?? '',
    address: data.address ?? '',
    pincode: data.pincode ?? '',
    city: data.city ?? '',
    state: data.state ?? '',
    area: data.area ?? null,
    clinicId: data.clinicId?._id || data.clinicId || null,
    ...(data._id ? { _id: data._id } : {}),
  });

  const getPhoneValue = () => (patientData.phone ?? '').trim();

  const isPhoneValid = (phone) => !phone || phoneRegex.test(phone);

  React.useEffect(() => {
    const fetchData = async () => {
      if (editData && Object.keys(editData).length > 0 && operationMode === 'Edit') {
        setPatientData(normalizePatient(editData));

        try {
          const response = await dispatch(postalApi({ pincode: editData.pincode }));

          if (
            response &&
            response.payload &&
            response.payload[0] &&
            response.payload[0].PostOffice
          ) {
            setAreaOptions(
              response.payload[0].PostOffice.map((item) => ({
                label: item.Name,
                value: item.Name,
              })),
            );
          }
        } catch (error) {
          console.error('Error fetching postal data', error);
        }
      } else {
        setPatientData({
          name: '',
          phone: '',
          gender: 'Male',
          age: '',
          occupation: '',
          address: '',
          pincode: '',
          city: '',
          state: '',
          area: null,
          clinicId: null,
        });
        setOperationMode('Add');
      }
    };

    fetchData();
  }, [editData, operationMode]);

  const handleClose = () => {
    setOpen(false);
    setPatientData({
      name: '',
      phone: '',
      gender: 'Male',
      age: '',
      occupation: '',
      address: '',
      pincode: '',
      city: '',
      state: '',
      area: null,
      clinicId: null,
    });
    setOperationMode('Add');
  };

  const handleOnChange = async (e, newValue) => {
    // Handle Autocomplete (area)
    if (e && e.target && e.target.name === 'area') {
      setPatientData((prev) => ({
        ...prev,
        area: newValue ? newValue.value : null,
      }));
      return;
    }

    const { name, value } = e.target;

    // 👉 PHONE VALIDATION (India)
    if (name === 'phone') {
      // Remove non-digits
      let cleaned = value.replace(/\D/g, '');

      // Prevent more than 10 digits
      if (cleaned.length > 10) return;

      // First digit must be 6-9
      if (cleaned.length === 1 && !/[6-9]/.test(cleaned)) {
        return; // block invalid start like 1,2,3,4,5
      }

      let errorMsg = '';
      if (cleaned.length > 0 && !phoneRegex.test(cleaned)) {
        errorMsg = 'Mobile must start with 6–9 and be 10 digits';
      }

      setPatientData((prev) => ({
        ...prev,
        phone: cleaned,
      }));

      setErrors((prev) => ({
        ...prev,
        phone: errorMsg,
      }));

      return;
    }

    // Handle pincode and auto-fetch city/state
    // 👉 PINCODE VALIDATION (6 digits only)
    if (name === 'pincode') {
      // allow only digits
      let cleaned = value.replace(/\D/g, '');

      // prevent more than 6 digits
      if (cleaned.length > 6) return;

      setPatientData((prev) => ({
        ...prev,
        pincode: cleaned,
      }));

      // Call postal API ONLY when exactly 6 digits entered
      if (cleaned.length === 6) {
        const response = await dispatch(postalApi({ pincode: cleaned }));

        if (response?.payload?.[0]?.PostOffice) {
          const city = response.payload[0].PostOffice[0].District;
          const state = response.payload[0].PostOffice[0].State;

          setAreaOptions(
            response.payload[0].PostOffice.map((item) => ({
              label: item.Name,
              value: item.Name,
            })),
          );

          setPatientData((prev) => ({
            ...prev,
            pincode: cleaned,
            city,
            state,
          }));
        }
      } else {
        // Reset if user deletes digits
        setPatientData((prev) => ({
          ...prev,
          pincode: cleaned,
          city: '',
          state: '',
          area: null,
        }));
      }

      return;
    }

    if (name === 'age') {
      // allow only digits
      let cleaned = value.replace(/\D/g, '');

      // prevent more than 3 digits
      if (cleaned.length > 3) return;

      // convert to number to validate range
      const num = Number(cleaned);

      if (cleaned && num > 130) return;

      setPatientData((prev) => ({
        ...prev,
        age: cleaned,
      }));

      return;
    }

    // Generic field update
    setPatientData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!patientData.clinicId) {
      return toast.error('Please select clinic');
    }
    if (!(patientData.name ?? '').trim()) {
      return toast.error('Please enter name');
    }

    const phone = getPhoneValue();
    if (phone && !phoneRegex.test(phone)) {
      return toast.error('Enter valid 10-digit mobile starting with 6–9');
    }

    const payload = {
      ...normalizePatient(patientData),
      phone: phone || null,
    };

    const response = await dispatch(
      operationMode == 'Add'
        ? addPatient(payload)
        : updatePatient({ ...payload, _id: patientData._id }),
    );
    // if (!response.payload?.error) {
    //   handleClose();
    //   toast.success(response.payload?.message);
    // }
    if (response.type.endsWith('/fulfilled')) {
      toast.success(response.payload?.message || 'Success');
      handleClose();
      callApi();
    }

    // ❌ ERROR
    if (response.type.endsWith('/rejected')) {
      toast.error(response.payload?.message || 'Something went wrong');
    }

    callApi();
  };

  const phone = getPhoneValue();
  const isDisabled =
    !patientData.clinicId ||
    !(patientData.name ?? '').trim() ||
    (phone.length > 0 && !isPhoneValid(phone)) ||
    !!errors.phone;

  return (
    <React.Fragment>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2, border: '1px solid var(--color-border)' } }}
      >
        <DialogTitle sx={{ py: 1.1, px: 2, fontSize: '1rem', fontWeight: 800, color: 'var(--color-heading)' }}>
          {operationMode} Patient
        </DialogTitle>
        <DialogContent sx={{ px: 2, pt: 0.2, pb: 1 }}>
          <div style={{ margin: '10px 0' }}>
            <SearchClinic
              open={open}
              setData={setPatientData}
              data={patientData}
              name="clinicId"
              label="Clinic"
              size="small"
            />
          </div>
          <div style={{ marginTop: '15px' }}>
            <CustomTextField
              label="Name"
              size="small"
              fullWidth
              className={PatientStyle.input}
              name="name"
              value={patientData?.name}
              onChange={handleOnChange}
              required
            />
          </div>
          <div>
            {/* <CustomTextField
              label="Age"
              size="small"
              fullWidth
              className={PatientStyle.input}
              name="age"
              value={patientData?.age}
              onChange={handleOnChange}
              type="number"
            /> */}
            <CustomTextField
              label="Age"
              size="small"
              fullWidth
              className={PatientStyle.input}
              name="age"
              value={patientData?.age}
              onChange={handleOnChange}
              type="text"
              inputProps={{
                inputMode: 'numeric',
                min: 0,
                max: 130,
              }}
            />
          </div>
          <div>
            {/* <FormControl>
              <FormLabel id="demo-row-radio-buttons-group-label">Gender</FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                defaultValue={patientData?.gender}
              >
                <FormControlLabel value="Male" control={<Radio />} label="Male" />
                <FormControlLabel value="Female" control={<Radio />} label="Female" />
                <FormControlLabel value="Other" control={<Radio />} label="Other" />
              </RadioGroup>
            </FormControl> */}
            <FormControl>
              <FormLabel>Gender</FormLabel>

              <RadioGroup row name="gender" value={patientData.gender} onChange={handleOnChange}>
                <FormControlLabel value="Male" control={<Radio />} label="Male" />
                <FormControlLabel value="Female" control={<Radio />} label="Female" />
                <FormControlLabel value="Other" control={<Radio />} label="Other" />
              </RadioGroup>
            </FormControl>
          </div>
          <div>
            <CustomTextField
              label="Phone Number"
              size="small"
              fullWidth
              className={PatientStyle.input}
              name="phone"
              value={patientData?.phone ?? ''}
              onChange={handleOnChange}
              type="text"
              error={!!errors.phone}
              helperText={errors.phone}
              inputProps={{
                maxLength: 10,
                inputMode: 'numeric',
              }}
            />
          </div>
          <div>
            <CustomTextField
              label="Occupation"
              size="small"
              fullWidth
              className={PatientStyle.input}
              name="occupation"
              value={patientData?.occupation}
              onChange={handleOnChange}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <CustomTextField
              label="Pincode"
              size="small"
              className={PatientStyle.input}
              name="pincode"
              value={patientData?.pincode}
              onChange={handleOnChange}
              sx={{ width: '50%' }}
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric',
              }}
            />
            <CustomTextField
              label="City"
              size="small"
              className={PatientStyle.input}
              name="city"
              value={patientData?.city}
              onChange={handleOnChange}
              sx={{ width: '50%' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <CustomTextField
              label="State"
              size="small"
              className={PatientStyle.input}
              name="state"
              value={patientData?.state}
              onChange={handleOnChange}
              sx={{ width: '50%' }}
            />
            <Autocomplete
              disablePortal
              name="area"
              options={areaOptions}
              size="small"
              sx={{ width: '50%' }}
              value={patientData?.area || null}
              onChange={(e, newValue) => handleOnChange({ target: { name: 'area' } }, newValue)}
              renderInput={(params) => <TextField {...params} label="Area" />}
            />
          </div>
          <div>
            <CustomTextField
              size="small"
              label="Address"
              fullWidth
              className={PatientStyle.input}
              multiline
              rows={3}
              name="address"
              value={patientData?.address}
              onChange={handleOnChange}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClose}
            color="inherit"
            sx={{ textTransform: 'none', borderColor: 'var(--color-border-soft)' }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={loading}
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={isDisabled}
            sx={{
              textTransform: 'none',
              minWidth: 92,
              background: isDisabled ? 'var(--gradient-disabled)' : 'var(--gradient-primary)',
            }}
          >
            {operationMode === 'Edit' ? 'Update' : 'Submit'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
